<script setup>
import { defineProps, inject, ref, onMounted, computed } from "vue";
import emitter from "@/utils/bus";
import facebook_icon from "./icon/facebook.svg";
import copy_icon from "@/assets/icon/copy.svg";
import messenger_icon from "./icon/messenger.svg";
import whatsapp_icon from "./icon/whatsapp.svg";
import x_icon from "./icon/x.svg";
import telegram_icon from "./icon/telegram.svg";
import line_icon from "./icon/line.svg";
import { useI18n } from "vue-i18n";
import { useConfigStore } from "@/store/config";
import { fetchShareId } from "@/api/user";
import { fetchShareDomain } from "@/api/share"
const { t } = useI18n();
import {
  appHapticFeedback,
  toastSuccess,
  toastFail,
  base62Encode,
  getCacheV1,
  setCacheV1,
  isSupportedVersion,
} from "@/utils/common";
const props = defineProps({
  url: {
    type: String,
    required: true,
  },

  /**
   * 用户的twa_id
   */
  twa_id: {
    type: String,
    required: false,
  },
  /**
   * 分享的内容
   */
  title: {
    type: String,
    required: false,
  },
  /**
   * 是否显示长按钮
   */
  long_btn: {
    type: Boolean,
    required: false,
  },
});
const $swal = inject("$swal");
const config = useConfigStore();
const themes_color = computed(() => {
  // console.log(config.themes_color);

  return config.themes_color;
});

const share_domain = ref("");
let share_id = ref("");
// 展示和复制使用同一条链接，保留原有的邀请来源标记。
const copyShareUrl = computed(() => {
  const appid = TApp_GlobalConfig.project_id;
  return `https://${share_domain.value}/go?i=${appid}_${props.twa_id}_copy`;
});
const toWhatsapp = () => {
  appHapticFeedback();

  let appid = TApp_GlobalConfig.project_id; // 项目id
  let share_url = `https://${share_domain.value}/go?i=${appid}_${props.twa_id}_wa`;
  window.Telegram.WebApp.openLink(
    "https://api.whatsapp.com/send?text=" +
    encodeURIComponent(props.title) +
    " " +
    encodeURIComponent(share_url)
  );
};
const toFacebook = () => {
  appHapticFeedback();
  let appid = TApp_GlobalConfig.project_id; // 项目id
  let share_url = `https://${share_domain.value}/go?i=${appid}_${props.twa_id}_fb`;
  window.Telegram.WebApp.openLink(
    "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(share_url)
  );
};
const toMessenger = () => {
  appHapticFeedback();
  let appid = TApp_GlobalConfig.project_id; // 项目id
  let share_url = `https://${share_domain.value}/go?i=${appid}_${props.twa_id}_msgr`;
  window.open("fb-messenger://share/?link=" + encodeURIComponent(share_url));
};
const toLine = () => {
  appHapticFeedback();
  let appid = TApp_GlobalConfig.project_id; // 项目id
  let share_url = `https://${share_domain.value}/go?i=${appid}_${props.twa_id}_line`;
  window.Telegram.WebApp.openLink(
    "https://line.me/R/share?text=" +
    encodeURIComponent(props.title) +
    " " +
    encodeURIComponent(share_url)
  );
};
const toTwitter = () => {
  appHapticFeedback();
  let appid = TApp_GlobalConfig.project_id; // 项目id
  let share_url = `https://${share_domain.value}/go?i=${appid}_${props.twa_id}_x`;
  window.Telegram.WebApp.openLink(
    "https://twitter.com/intent/tweet?url=" +
    encodeURIComponent(share_url) +
    "&text=" +
    encodeURIComponent(props.title)
  );
};
const toLink = async () => {
  appHapticFeedback();
  try {
    await navigator.clipboard.writeText(copyShareUrl.value);
    toastSuccess($swal, t("message.copySuccess"));
  } catch (error) {
    toastFail($swal, t("message.copyFailure"));
  }
};

// 内部分享
const toTelegram = async () => {
  appHapticFeedback();
  if (share_id.value) {
    window.Telegram.WebApp.shareMessage(share_id.value);
    return;
  }
  let share_url = props.url + "?startapp=tg_" + props.twa_id;
  window.Telegram.WebApp.openTelegramLink(
    "https://t.me/share/url?text=" +
    encodeURIComponent(props.title) +
    "&url=" +
    encodeURIComponent(share_url)
  );
};

emitter.on("share", () => {
  toTelegram();
});

onMounted(async () => {
  share_id.value = getCacheV1("share_id"); // 本地获取缓存
  if (!share_id.value && isSupportedVersion("8.0")) {
    let res = await fetchShareId({
      title: props.title,
    }); // 请求分享ID
    if (res.code == 200) {
      setCacheV1("share_id", res.data, 50);
      share_id.value = res.data;
    }
  }

  let res = await fetchShareDomain();
  if (res.code == 200) {
    share_domain.value = res.data;
  }
});
</script>
<template>
  <div class="share-options">
    <div class="share-btn" v-show="props.long_btn">
      <div class="left" @click="toTelegram">
        {{ $t("message.Sharewithfriends") }}
      </div>
      <div class="right" @click="toTelegram">
        <img src="@/assets/imgs/finger2.png" alt="" class="finger" />
      </div>
    </div>
    <div v-if="share_domain" class="share-link-row">
      <span class="share-link-text" dir="ltr" :title="copyShareUrl">{{ copyShareUrl }}</span>
      <button type="button" class="share-copy-button" :aria-label="$t('message.copy')" @click="toLink">
        <img :src="copy_icon" alt="" />
      </button>
    </div>
    <div class="share-icons">
      <div class="share-icon telegram" @click="toTelegram" v-show="!props.long_btn">
        <img :src="telegram_icon" />
      </div>
      <div class="share-icon whatsapp" @click="toWhatsapp">
        <img :src="whatsapp_icon" />
      </div>
      <!-- <div class="share-icon facebook" @click="toFacebook">
        <img :src="facebook_icon" />
      </div> -->
      <div class="share-icon messenger" @click="toMessenger">
        <img :src="messenger_icon" />
      </div>
      <div class="share-icon line" @click="toLine">
        <img :src="line_icon" />
      </div>
      <div class="share-icon twitter" @click="toTwitter">
        <img :src="x_icon" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/surfaces" as surface;

.share-options {
  display: flex;
  flex-direction: column;
  width: 100%;
  animation: fadeInUp 0.5s ease;

  .share-btn {
    margin: 0 auto 8px;
    font-weight: bold;
    width: 100%;
    min-height: var(--home-button-height, 36px);
    background: v-bind("themes_color");
    border-radius: var(--home-button-radius, 10px);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 8px 44px 8px 10px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(145deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%);
      z-index: 1;
    }

    .left {
      @include surface.press-feedback;
      line-height: 1.4;
      font-size: var(--home-font-button, 14px);
      position: relative;
      overflow-wrap: anywhere;
      font-weight: 700;
      color: white;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      z-index: 2;
    }

    .right {
      @include surface.press-feedback;
      position: absolute;
      right: 8px;
      width: 36px;
      height: 36px;
      z-index: 2;

      .finger {
        width: 32px;
        position: absolute;
        top: 6px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 99;
        animation: pulse 1s infinite alternate;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
      }
    }
  }
}

.share-link-row {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: var(--home-button-height, 36px);
  margin-top: 0;
  overflow: hidden;
  border: 1px solid #3c3c3c;
  border-radius: var(--home-button-radius, 10px);
  background: #151515;
}

.share-link-text {
  flex: 1;
  min-width: 0;
  padding: 0 10px;
  overflow: hidden;
  color: #e0e0e0;
  font-size: var(--home-font-body, 14px);
  line-height: 1.5;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: text;
  -webkit-user-select: text;
}

.share-copy-button {
  @include surface.press-feedback($focus-offset: -3px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: stretch;
  width: var(--home-button-height, 36px);
  min-height: var(--home-button-height, 36px);
  padding: 0;
  border: 0;
  border-left: 1px solid #3c3c3c;
  background: #2b2b2b;
  cursor: pointer;

  img {
    width: 20px;
    height: 20px;
  }
}

.share-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: clamp(4px, 1.6vw, 8px);
  flex: 1;
  padding: 0;
  margin-top: 8px;
}

.share-icon {
  @include surface.press-feedback;
  width: var(--home-icon-button-size, 36px);
  height: var(--home-icon-button-size, 36px);
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--home-button-radius, 10px);
  font-size: 24px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.2) 100%);
    z-index: 1;
  }

  img {
    width: 20px;
    height: 20px;
    position: relative;
    z-index: 2;
  }
}

.twitter {
  background: #000000 !important;
}

.telegram {
  background: #0099ff !important;
}

.facebook {
  background: #18b0ff !important;
}

.whatsapp {
  background: #25d366 !important;
}

.messenger {
  background: #0099ff !important;
}

.line {
  background: #00c300 !important;
}

@keyframes pulse {
  0% {
    transform: scale(1) translateX(-50%);
  }

  100% {
    transform: scale(1.2) translateX(-50%);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
